import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Using Tenor's free API (no key required for basic usage)
const TENOR_API_KEY = 'AIzaSyAyimkuYQYF_FXVALexPuGQctUWRURdCYQ'; // Public Tenor API key
const TENOR_BASE_URL = 'https://tenor.googleapis.com/v2';

const GifPicker = ({ onSelect, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);

  // Fetch trending GIFs on mount
  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${TENOR_BASE_URL}/featured?key=${TENOR_API_KEY}&limit=20&media_filter=gif`
      );
      const data = await response.json();
      setTrending(data.results || []);
      setGifs(data.results || []);
    } catch (error) {
      console.error('Failed to fetch trending GIFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchGifs = async () => {
    if (!searchQuery.trim()) {
      setGifs(trending);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${TENOR_BASE_URL}/search?key=${TENOR_API_KEY}&q=${encodeURIComponent(searchQuery)}&limit=20&media_filter=gif`
      );
      const data = await response.json();
      setGifs(data.results || []);
    } catch (error) {
      console.error('Failed to search GIFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (gif) => {
    // Get the smallest GIF format for better performance
    const gifUrl = gif.media_formats?.gif?.url || 
                   gif.media_formats?.tinygif?.url ||
                   gif.media_formats?.mediumgif?.url;
    
    if (gifUrl) {
      onSelect({
        url: gifUrl,
        width: gif.media_formats?.gif?.dims?.[0] || 200,
        height: gif.media_formats?.gif?.dims?.[1] || 200,
        id: gif.id
      });
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  const debouncedSearch = useCallback(debounce(searchGifs, 500), [searchQuery]);

  useEffect(() => {
    if (searchQuery) {
      debouncedSearch();
    } else {
      setGifs(trending);
    }
  }, [searchQuery]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="absolute bottom-full left-0 mb-2 w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
      data-testid="gif-picker"
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-gray-900 dark:text-white">Choose a GIF</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full p-1 h-8 w-8"
            data-testid="gif-picker-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search GIFs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 rounded-full h-9 text-sm"
            data-testid="gif-search-input"
          />
        </div>
      </div>

      {/* GIF Grid */}
      <div className="h-64 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : gifs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            No GIFs found
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {gifs.map((gif) => (
              <motion.button
                key={gif.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSelect(gif)}
                className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-square"
                data-testid={`gif-${gif.id}`}
              >
                <img
                  src={gif.media_formats?.tinygif?.url || gif.media_formats?.gif?.url}
                  alt={gif.content_description || 'GIF'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {/* Powered by Tenor */}
      <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-center">
        <span className="text-xs text-gray-500">Powered by Tenor</span>
      </div>
    </motion.div>
  );
};

export default GifPicker;
